/* eslint-disable @typescript-eslint/no-explicit-any */
import { ILocalComms, Screen } from './interfaces/localComms';
import { OrderEvent } from '@oolio-group/domain';
import { setEnableLocalComms } from '../../state/preferences';
import { localCommsSubject } from '../../state/localComms';
import { isWeb } from '../environment';

// Minimal Wails bridge typings for the methods we use here
declare global {
  interface Window {
    go?: {
      main?: {
        App?: {
          InitOrdersSDK?: (config: unknown) => Promise<void>;
          StartOrdersSDK?: () => Promise<void>;
          Refresh?: () => Promise<unknown>;
          SetNodeMetadata?: (metadata: string) => void;
          ShareLogFile?: () => Promise<boolean>;
          ExportEventsDbAndGetFileName?: () => Promise<string>;
          UpdateGQLClientHeaders?: (headers: string) => Promise<void>;
          ShutdownSDK?: () => Promise<void>;
          SyncPendingOrderEvents?: () => Promise<void>;
          AddOrderEvents?: (
            events: string,
            skipPushToCloud: boolean,
          ) => Promise<unknown>;
          SubscribeOrderEventsStream?: (topic: string) => Promise<unknown>;
          SubscribeAppEventsStream?: (topic: string) => Promise<unknown>;
          PushAppEventsToStream?: (
            subject: string,
            event: string,
          ) => Promise<unknown>;
          SubscribeStatus?: () => Promise<string>;
          ToggleFullScreen?: () => Promise<void>;
          GetAvailableScreens?: () => Promise<Screen[]>;
          MoveToSecondScreen?: () => Promise<boolean>;
          GetScreenPreference?: () => Promise<number>;
          SetScreenPreference?: (screenIndex: number) => Promise<void>;
          MoveToScreen?: (screenIndex: number) => Promise<void>;
          GetAccessToken?: () => Promise<string>;
          SubmitTip?: (answer: unknown) => Promise<unknown>;
        };
      };
    };
    runtime?: {
      EventsOn?: (topic: string, cb: (data: unknown) => void) => void;
      EventsOff?: (topic: string) => void;
    };
  }
}

/**
 * Simplified Desktop Orders SDK
 * - Matches the mobile implementation surface
 * - Keeps the same public methods and the isEnabled behavior
 * - Gracefully no-ops when a Wails/bridge is not present
 */
class DesktopOrdersSDK implements ILocalComms {
  // keep boolean to satisfy ILocalComms contract; preferences getter is async so
  // we don't await it here. Consumers should call setIsEnabled if needed.
  isEnabled = false;
  // device status and listeners for desktop
  private deviceStatus = {
    status: 'disconnected',
    queue: 0,
    nodes: [],
    isLocalCommunicationAvailable: false,
    isConnected: false,
  } as any;
  private statusListeners: Set<(s: any) => void> = new Set();
  private statusCleanup?: () => void;

  async init(
    gqlClientName: string,
    gqlClientVersion: string,
    serviceUrl: string,
  ): Promise<void> {
    try {
      // eslint-disable-next-line no-console
      console.log(
        '[LocalComms] init() called',
        gqlClientName,
        gqlClientVersion,
        serviceUrl,
      );

      // If a Wails bridge exists and exposes InitOrdersSDK, forward the call with the Wails config.
      if (
        typeof window !== 'undefined' &&
        window.go?.main?.App?.InitOrdersSDK
      ) {
        const wailsConfig = {
          ServerName: 'OrdersSDK-CDS',
          MdnsServiceTag: 'oolio-discovery',
          StoragePath: '/tmp/orders-sdk-cds',
          EnableLogging: true,
          StreamName: 'orders-stream',
          GraphQLUrl: serviceUrl,
          GQLClientName: gqlClientName,
          GQLClientVersion: gqlClientVersion,
          LogFile: 'orders-sdk.log',
          DisableNATS: false,
          ServerPort: '5222',
          ClusterPort: '7222',
          RouteConfigJSON: JSON.stringify({
            server: '4222',
            client: '6222',
          }),
        };

        // eslint-disable-next-line no-console
        console.log(
          '[LocalComms] calling InitOrdersSDK on Go side with wailsConfig keys:',
          Object.keys(wailsConfig),
        );

        await window.go.main.App.InitOrdersSDK(wailsConfig as unknown);
        // eslint-disable-next-line no-console
        console.log('[LocalComms] InitOrdersSDK completed');

        // Mark SDK enabled and attempt to start it so the Go side can begin
        // emitting status updates. Also register runtime handlers here so we
        // do not need to poll later in subscribeToDeviceStatus.
        this.setIsEnabled(true);
        // best-effort start; StartOrdersSDK may not be necessary but will
        // cause the backend to begin emitting status updates.
        void this.start();

        // Register runtime status handlers now that init/start completed.
        // If window.runtime isn't available immediately, the EventsOn calls
        // will be no-ops; the Go side emits status events once started.
        if (typeof window !== 'undefined' && window.runtime?.EventsOn) {
          // eslint-disable-next-line no-console
          console.log(
            '[LocalComms] runtime.EventsOn available; registering sdkStatus handlers',
          );

          const handler = (data: unknown) => {
            try {
              const parsed =
                typeof data === 'string' ? JSON.parse(data) : (data as any);

              const actual =
                parsed?.status && typeof parsed.status === 'string'
                  ? JSON.parse(parsed.status)
                  : parsed;

              this.deviceStatus = {
                status: actual.status || 'unknown',
                queue: actual.queue || 0,
                nodes: actual.nodes || [],
                isLocalCommunicationAvailable:
                  actual.isLocalCommunicationAvailable || false,
                isConnected: actual.isConnected || false,
              };

              this.statusListeners.forEach(l => {
                try {
                  l({ ...this.deviceStatus });
                } catch (e) {
                  // ignore listener errors
                }
              });
            } catch (e) {
              console.warn('[LocalComms] failed to parse sdkStatus payload', e);
            }
          };

          window.runtime.EventsOn('sdkStatus', handler);
          window.runtime.EventsOn('sdk-status', handler);

          // After we've registered runtime handlers, tell the Go side to
          // subscribe to SDK status updates so it will invoke the
          // SDKStatusCallbackImpl and emit runtime events.
          try {
            if (
              typeof window !== 'undefined' &&
              window.go?.main?.App?.SubscribeStatus
            ) {
              // eslint-disable-next-line no-console
              console.log('[LocalComms] calling SubscribeStatus on Go side');

              // best-effort; SubscribeStatus returns a subscription id
              void window.go.main.App.SubscribeStatus();

              // eslint-disable-next-line no-console
              console.log('[LocalComms] SubscribeStatus invoked');
            }
          } catch (e) {
            console.warn('[LocalComms] SubscribeStatus invocation failed', e);
          }

          this.statusCleanup = () => {
            try {
              const evOff = window.runtime?.EventsOff;
              if (evOff) {
                evOff('sdkStatus');
                evOff('sdk-status');
              }
            } catch (e) {
              // ignore
            }
          };
        }
      }
    } catch (e) {
      // swallow - desktop init is best-effort in this simplified version
      // callers can check isEnabled to know if local comms are active
      // eslint-disable-next-line no-console
      console.warn('DesktopOrdersSDK.init: bridge init failed', e);
    }
  }

  async start(): Promise<void> {
    try {
      if (
        this.isEnabled &&
        typeof window !== 'undefined' &&
        window.go?.main?.App?.StartOrdersSDK
      ) {
        await window.go.main.App.StartOrdersSDK();
      }
    } catch (e) {
      // best-effort start
      // eslint-disable-next-line no-console
      console.warn('DesktopOrdersSDK.start failed', e);
    }
  }

  async restart(): Promise<boolean | Error> {
    if (!this.isEnabled) return false;
    try {
      if (typeof window !== 'undefined' && window.go?.main?.App?.Refresh) {
        const res = await window.go.main.App.Refresh();
        return !(res instanceof Error);
      }
      return true;
    } catch (e) {
      return e instanceof Error ? e : new Error('Restart failed');
    }
  }

  setMetadata(metadata: unknown): void {
    if (!this.isEnabled) return;
    if (
      typeof window !== 'undefined' &&
      window.go?.main?.App?.SetNodeMetadata
    ) {
      window.go.main.App.SetNodeMetadata(JSON.stringify(metadata));
    }
  }

  async shareLog(): Promise<boolean> {
    try {
      if (!this.isEnabled) return false;
      if (typeof window !== 'undefined' && window.go?.main?.App?.ShareLogFile) {
        return await window.go.main.App.ShareLogFile();
      }
      return false;
    } catch {
      return false;
    }
  }

  async exportDatabase(): Promise<boolean> {
    try {
      if (!this.isEnabled) return false;
      if (
        typeof window !== 'undefined' &&
        window.go?.main?.App?.ExportEventsDbAndGetFileName
      ) {
        const result = await window.go.main.App.ExportEventsDbAndGetFileName();
        return typeof result === 'string' && result.length > 0;
      }
      return false;
    } catch {
      return false;
    }
  }

  updateHeaders(headers: Record<string, string>): void {
    if (!this.isEnabled) return;
    if (
      typeof window !== 'undefined' &&
      window.go?.main?.App?.UpdateGQLClientHeaders
    ) {
      window.go.main.App.UpdateGQLClientHeaders(JSON.stringify(headers));
    }
  }

  setIsEnabled(isEnabled: boolean): void {
    this.isEnabled = isEnabled;
    setEnableLocalComms(isEnabled);
    localCommsSubject.next(isEnabled && !isWeb());
  }

  onLocalCommsToggleChange(isEnabled: boolean): void {
    const previous = this.isEnabled ?? false;
    if (previous === isEnabled) return;

    this.setIsEnabled(isEnabled);

    if (!isEnabled && previous) {
      // attempt best-effort shutdown
      if (typeof window !== 'undefined' && window.go?.main?.App?.ShutdownSDK) {
        void window.go.main.App.ShutdownSDK();
      }
      return;
    }

    if (isEnabled && !previous) {
      void this.start();
    }
  }

  async syncPendingEvents(): Promise<void> {
    if (!this.isEnabled) return;
    if (
      typeof window !== 'undefined' &&
      window.go?.main?.App?.SyncPendingOrderEvents
    ) {
      await window.go.main.App.SyncPendingOrderEvents();
    }
  }

  async addEvents(
    events: OrderEvent[],
    skipPushToCloud?: boolean,
  ): Promise<any | Error> {
    try {
      if (!this.isEnabled) return;
      if (
        typeof window !== 'undefined' &&
        window.go?.main?.App?.AddOrderEvents
      ) {
        return await window.go.main.App.AddOrderEvents(
          JSON.stringify(events),
          skipPushToCloud ?? false,
        );
      }
      return { data: { syncEvents: false } };
    } catch (e) {
      return e;
    }
  }

  async pushAppEventToStream(
    subject: string,
    event: unknown,
  ): Promise<null | Error> {
    try {
      if (!this.isEnabled) return new Error('LocalComms is not available');
      if (
        typeof window === 'undefined' ||
        !window.go?.main?.App?.PushAppEventsToStream
      ) {
        return new Error('LocalComms is not available');
      }
      await window.go.main.App.PushAppEventsToStream(
        subject,
        JSON.stringify(event),
      );
      return null;
    } catch (e) {
      return e instanceof Error ? e : new Error(String(e));
    }
  }

  // Hook-compatible: return current device status
  getLocalDeviceStatus(): any {
    return { ...this.deviceStatus };
  }

  // Subscribe to status updates from the Wails runtime
  subscribeToDeviceStatus(callback: (status: any) => void): () => void {
    this.statusListeners.add(callback);

    // ensure runtime listeners are set up. The Wails runtime may not be
    // ready at the moment this hook is called, so poll briefly until
    // EventsOn is available and then register handlers.
    if (!this.statusCleanup && typeof window !== 'undefined') {
      const registerHandler = () => {
        if (!window.runtime?.EventsOn) return false;

        const handler = (data: unknown) => {
          try {
            const parsed =
              typeof data === 'string' ? JSON.parse(data) : (data as any);

            const actual =
              parsed?.status && typeof parsed.status === 'string'
                ? JSON.parse(parsed.status)
                : parsed;

            this.deviceStatus = {
              status: actual.status || 'unknown',
              queue: actual.queue || 0,
              nodes: actual.nodes || [],
              isLocalCommunicationAvailable:
                actual.isLocalCommunicationAvailable || false,
              isConnected: actual.isConnected || false,
            };

            this.statusListeners.forEach(l => l({ ...this.deviceStatus }));
          } catch (e) {
            // ignore parse errors
          }
        };

        window.runtime.EventsOn('sdkStatus', handler);
        window.runtime.EventsOn('sdk-status', handler);

        this.statusCleanup = () => {
          const evOff = window.runtime?.EventsOff;
          if (evOff) {
            evOff('sdkStatus');
            evOff('sdk-status');
          }
        };

        return true;
      };

      // Immediate attempt, then poll every 100ms up to ~20 times (2s)
      if (!registerHandler()) {
        let attempts = 0;
        const maxAttempts = 20;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (registerHandler() || attempts >= maxAttempts) {
            clearInterval(timer as unknown as number);
          }
        }, 100);
      }
    }

    // call immediately with current status
    callback({ ...this.deviceStatus });

    return () => {
      this.statusListeners.delete(callback);
    };
  }

  async toggleFullScreen(): Promise<void> {
    try {
      if (
        typeof window !== 'undefined' &&
        window.go?.main?.App?.ToggleFullScreen
      ) {
        await window.go.main.App.ToggleFullScreen();
      }
    } catch (e) {
      console.warn('DesktopOrdersSDK.toggleFullScreen failed', e);
    }
  }

  async getAvailableScreens(): Promise<Screen[]> {
    try {
      if (
        typeof window !== 'undefined' &&
        window.go?.main?.App?.GetAvailableScreens
      ) {
        return (await window.go.main.App.GetAvailableScreens()) as Screen[];
      }
    } catch (e) {
      console.warn('DesktopOrdersSDK.getAvailableScreens failed', e);
    }
    return [];
  }

  async getScreenPreference(): Promise<number | null> {
    try {
      if (typeof window === 'undefined') {
        return -1; // -1 indicates no preference
      }
      if (!window.go?.main?.App?.GetScreenPreference) {
        return -1; // -1 indicates no preference
      }
      const result = await window.go.main.App.GetScreenPreference();
      return result; // Returns screen index or -1 for no preference
    } catch (e) {
      console.error('[LocalComms] getScreenPreference failed', e);
      return null; // null indicates error
    }
  }

  async setScreenPreference(screenIndex: number): Promise<void> {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      if (!window.go?.main?.App?.SetScreenPreference) {
        return;
      }
      await window.go.main.App.SetScreenPreference(screenIndex);
    } catch (e) {
      console.error('[LocalComms] setScreenPreference failed', e);
      throw e; // Re-throw to propagate error to caller
    }
  }

  async moveToScreen(screenIndex: number): Promise<void> {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      if (!window.go?.main?.App?.MoveToScreen) {
        return;
      }
      await window.go.main.App.MoveToScreen(screenIndex);
    } catch (e) {
      console.error('[LocalComms] moveToScreen failed', e);
      throw e; // Re-throw to propagate error to caller
    }
  }
}

export default DesktopOrdersSDK;
